/**
 * The screen: what happens when a thumb hits a button.
 *
 * ## The two rules that shape all of it
 *
 * **A tap stores the point immediately.** The answers, the note and the photos
 * are added afterwards to something already saved. Walking with a phone means
 * being interrupted — a car, a gate, a conversation — and a form that has to
 * be finished before anything is kept is a form that loses points.
 *
 * **The kind is chosen before the photo.** That is not just tidiness: knowing
 * it is a fountain is what lets the app ask whether it still runs, say how to
 * frame the shot, and keep improving the position while you stand there
 * taking it.
 *
 * ## What "improving the position" means
 *
 * The phone is asked for readings continuously and they are kept in a rolling
 * buffer. A point is the weighted average of the last few seconds, and while
 * its card is open the average keeps being recomputed. Standing still for ten
 * seconds is the single most useful thing the walker can do, so the screen
 * says so, and shows the count going up.
 */
import "./styles.css";
import {
  buildExport,
  estimateExportSize,
  localIso,
  pointId,
  type ExportItem,
  type ExportPhoto,
  type ExportPreviewItem,
} from "./bundle.js";
import { watchFix, type GeoProblem } from "./geo.js";
import { shrinkPhoto } from "./photo.js";
import { PENDIENTES, pendingKm, pendingSummary, type PendingSite } from "./pendientes.js";
import { allPoints, deleteMany, deletePoint, markExported, savePoint } from "./store.js";
import {
  averageSpot,
  isBetter,
  spotAgeS,
  spotQuality,
  type Sample,
  type Spot,
} from "./position.js";
import {
  CATEGORIES,
  hasOther,
  otherKey,
  readNumber,
  tagById,
  tagsIn,
  type Field,
  type Tag,
} from "./tags.js";
import type { Answers, StoredPoint } from "./types.js";
import {
  clockOf,
  countText,
  dayLabel,
  dayOf,
  daySummary,
  deleteWarning,
  bytesText,
  exportLabel,
  exportPreviewLabel,
  pendientesLabel,
  pointSummary,
  problemText,
  refiningText,
  spotDetail,
  spotWarning,
  statusText,
} from "./words.js";

/** Coordinates are kept to six decimals: about eleven centimetres. */
const COORD_DECIMALS = 6;

/**
 * How long **one** button ignores a second tap on itself.
 *
 * Walking, a thumb bounces, and two points of the same kind a tenth of a
 * second apart are never two things seen. The lock is per button on purpose:
 * a fountain right beside a milestone is two different taps within a second,
 * and swallowing the second one silently loses a real point.
 */
const DOUBLE_TAP_MS = 700;

/** Readings older than this are of no use to anybody and are thrown away. */
const BUFFER_MS = 40_000;

/**
 * How long a freshly pinned point keeps being improved.
 *
 * Long enough that standing still pays off, short enough that it has finished
 * by the time you have taken the photo and looked up.
 */
const REFINE_MS = 25_000;

/** More photos than this of one thing is an album, not a record. */
const MAX_PHOTOS = 3;
/** A stamp is one flat design: a second photo of it says nothing a first did not. */
const MAX_SELLO = 1;

/** Whether a tag has anything worth stamping — see `Tag.sello` in `tags.ts`. */
function hasSello(tag: Tag | null): boolean {
  return tag?.sello !== undefined;
}

/** Maps the internal quality names to the class names the stylesheet uses. */
const QUALITY_CLASS = { good: "bien", fair: "regular", bad: "mal" } as const;

function need<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (found === null) throw new Error(`Falta el elemento #${id}`);
  return found as T;
}

const el = {
  senal: need("senal"),
  margen: need("margen"),
  cuenta: need("cuenta"),
  botones: need("botones"),
  lista: need<HTMLUListElement>("lista"),
  exportar: need<HTMLButtonElement>("exportar"),
  limpiar: need<HTMLButtonElement>("limpiar"),
  abrirPendientes: need<HTMLButtonElement>("abrir-pendientes"),
  ficha: need<HTMLDialogElement>("ficha"),
  limpieza: need<HTMLDialogElement>("limpieza"),
  pendientes: need<HTMLDialogElement>("pendientes"),
};

/**
 * The one file input in the page, reused by every point.
 *
 * `capture` hands the job to the phone's own camera app, so this app never
 * touches the camera stream and never has to ask for that permission.
 */
const camera = document.createElement("input");
camera.type = "file";
camera.accept = "image/*";
camera.capture = "environment";
camera.className = "oculto";
document.body.appendChild(camera);

/** Every recent reading, newest last. */
const buffer: Sample[] = [];
let problem: GeoProblem | null = null;
let points: StoredPoint[] = [];
/** When each tag button starts accepting taps again. */
const lockedUntil = new Map<string, number>();
/** Object URLs handed to the thumbnails, so they can be given back. */
const thumbs = new Map<string, string[]>();

/**
 * The point exactly as the open card is editing it.
 *
 * Every tap, every keystroke and every photo lands here first, not in
 * `points` and not on the phone. Only "Guardar" copies it across. Closing any
 * other way — the back gesture, the escape key — simply drops it: nothing
 * about that fits an autosave, because the whole point of a save button is
 * that closing without touching it must leave nothing behind.
 *
 * Also stands in for "is a card open, and for which point": `draft === null`
 * means none is, and `draft.id` is the one that is.
 */
let draft: StoredPoint | null = null;
/** While a fresh point is still being improved: when to stop, and what to update. */
let refineUntil = 0;
let refineLine: HTMLParagraphElement | null = null;

function pending(): StoredPoint[] {
  return points.filter((point) => !point.exported);
}

/**
 * Pending sites (`pendientes.ts`) not yet linked to a captured point.
 *
 * A site counts as done from the moment its point exists, the same instant a
 * tap always saves one — whether or not "Guardar" was reached afterwards. It
 * is the same rule the rest of the app already follows: nothing is undone by
 * walking away from an open card.
 */
function pendingLeft(): PendingSite[] {
  const done = new Set(
    points.map((point) => point.pendingId).filter((id): id is string => id !== undefined),
  );
  return PENDIENTES.filter((site) => !done.has(site.id));
}

function round(value: number): number {
  return Number(value.toFixed(COORD_DECIMALS));
}

function find(id: string): StoredPoint | undefined {
  return points.find((candidate) => candidate.id === id);
}

/** The point's position, in the shape the quality rules expect. */
function spotOf(point: StoredPoint): Spot {
  return {
    lat: point.lat,
    lon: point.lon,
    accuracyM: point.accuracyM,
    spreadM: point.spreadM,
    samples: point.samples,
    altitudeM: point.altitudeM,
    newestAt: 0,
  };
}

/* --------------------------------------------------------- the status bar */

function renderStatus(): void {
  const now = Date.now();
  const spot = averageSpot(buffer, now);
  const stuck = problem !== null && (spot === null || problem === "denied");

  if (spot === null || stuck) {
    el.senal.className = "senal";
    el.margen.textContent = problem === null ? "Buscando dónde estás…" : problemText(problem);
  } else {
    const age = spotAgeS(spot, now);
    el.senal.className = `senal ${QUALITY_CLASS[spotQuality(spot, age)]}`;
    el.margen.textContent = statusText(spot, age);
  }
  el.cuenta.textContent = countText(points.length, pending().length);
}

/* ------------------------------------------------------------- the buttons */

/**
 * The buttons, grouped into the four sections `tags.ts` defines.
 *
 * Thirteen buttons in one grid would still work, but four short, named groups
 * read faster than one long scroll of unrelated things, and match how a walk
 * actually splits: staying on the trail, stopping somewhere, what the village
 * offers, and what might spoil the day.
 */
function renderButtons(): void {
  el.botones.textContent = "";
  for (const category of CATEGORIES) {
    const tags = tagsIn(category.id);
    if (tags.length === 0) continue;

    const heading = document.createElement("h3");
    heading.className = "categoria";
    heading.textContent = category.label;
    el.botones.appendChild(heading);

    const grid = document.createElement("div");
    grid.className = "botones";
    for (const tag of tags) {
      const button = document.createElement("button");
      button.type = "button";
      button.className = "boton";

      const emoji = document.createElement("span");
      emoji.className = "emoji";
      emoji.textContent = tag.emoji;
      const name = document.createElement("span");
      name.className = "nombre";
      name.textContent = tag.label;
      const hint = document.createElement("span");
      hint.className = "pista";
      hint.textContent = tag.hint;

      button.append(emoji, name, hint);
      button.addEventListener("click", () => void capture(tag));
      grid.appendChild(button);
    }
    el.botones.appendChild(grid);
  }
}

/* ---------------------------------------------------------------- the list */

function releaseThumbs(): void {
  for (const urls of thumbs.values()) for (const url of urls) URL.revokeObjectURL(url);
  thumbs.clear();
}

function renderList(): void {
  releaseThumbs();
  el.lista.textContent = "";

  if (points.length === 0) {
    const empty = document.createElement("li");
    empty.className = "vacio";
    empty.textContent = "Todavía no has apuntado nada. Toca lo que veas.";
    el.lista.appendChild(empty);
    return;
  }

  for (const point of [...points].reverse()) {
    const tag = tagById(point.tag);
    const quality = spotQuality(spotOf(point), point.fixAgeS);

    const row = document.createElement("button");
    row.type = "button";
    row.className = `fila punto-${QUALITY_CLASS[quality]}`;

    const emoji = document.createElement("span");
    emoji.className = "emoji";
    emoji.textContent = tag?.emoji ?? "•";

    const what = document.createElement("span");
    what.className = "que";
    what.textContent = pointSummary(tag?.label ?? point.tag, point.fields);

    const when = document.createElement("span");
    when.className = "cuando";
    when.textContent =
      `${clockOf(point.takenAt)} · ${point.accuracyM} m` +
      (point.photos.length > 1 ? ` · ${point.photos.length} fotos` : "") +
      (point.exported ? " · exportado" : "") +
      (point.note === "" ? "" : ` · ${point.note}`);

    const text = document.createElement("span");
    text.className = "texto";
    text.append(what, document.createElement("br"), when);
    row.append(emoji, text);

    // A general photo makes a better thumbnail than a close-up of a stamp;
    // fall back to whatever there is rather than show nothing.
    const first = point.photos.find((photo) => photo.kind === "general") ?? point.photos[0];
    if (first !== undefined) {
      const url = URL.createObjectURL(first.blob);
      thumbs.set(point.id, [url]);
      const image = document.createElement("img");
      image.className = "miniatura";
      image.src = url;
      image.alt = "";
      row.appendChild(image);
    }

    row.addEventListener("click", () => openSheet(point.id));
    const item = document.createElement("li");
    item.appendChild(row);
    el.lista.appendChild(item);
  }
}

function renderFooter(): void {
  const count = pending().length;
  const going = pending();
  const previewItems: ExportPreviewItem[] = going.map((stored) => {
    const { photos, exported: _exported, pendingId: _pendingId, ...point } = stored;
    return { point, photos: photos.map((photo) => ({ kind: photo.kind, bytes: photo.blob.size })) };
  });
  const photoCount = previewItems.reduce((total, item) => total + item.photos.length, 0);
  const size = estimateExportSize(previewItems, new Date());
  el.exportar.textContent =
    count === 0
      ? exportLabel(count, navigator.onLine)
      : exportPreviewLabel(count, photoCount, size, navigator.onLine);
  el.exportar.disabled = count === 0;
  el.abrirPendientes.textContent = pendientesLabel(pendingLeft().length);
}

function render(): void {
  renderList();
  renderFooter();
  renderStatus();
}

/* --------------------------------------------------------------- capturing */

/**
 * Pins a point of this tag right now, and returns its id.
 *
 * `null` when nothing was pinned — the double-tap lock or a missing position
 * both bail out before saving anything, and there is then no point for a
 * caller to attach anything to.
 *
 * `startingNote` is the one thing a pending site (`pendientes.ts`) hands
 * over up front — its name, if the registry had one — without changing what
 * a plain tap does: the point is still saved before anything is asked, same
 * as always.
 */
async function capture(tag: Tag, startingNote?: string): Promise<string | null> {
  const now = Date.now();
  if (now < (lockedUntil.get(tag.id) ?? 0)) return null;
  lockedUntil.set(tag.id, now + DOUBLE_TAP_MS);

  const spot = averageSpot(buffer, now);
  if (spot === null) {
    window.alert(
      problem === null
        ? "Todavía no sé dónde estás. Espera a que la luz de arriba se encienda."
        : problemText(problem),
    );
    return null;
  }

  const at = new Date();
  const point: StoredPoint = {
    id: pointId(at),
    tag: tag.id,
    fields: {},
    note: startingNote ?? "",
    lat: round(spot.lat),
    lon: round(spot.lon),
    accuracyM: spot.accuracyM,
    spreadM: spot.spreadM,
    samples: spot.samples,
    fixAgeS: Math.round(spotAgeS(spot, at.getTime())),
    altitudeM: spot.altitudeM,
    takenAt: localIso(at),
    perishable: tag.perishable,
    photos: [],
    exported: false,
  };

  await savePoint(point);
  points = [...points, point];
  render();
  refineUntil = Date.now() + REFINE_MS;
  openSheet(point.id);
  return point.id;
}

/**
 * Starts a pending site the same way any other tap starts a point: saved at
 * once, seeded with the name the registry already had, and its own card open
 * to answer what that registry was missing.
 *
 * The link back to the registry (`pendingId`) is set on the draft, not on the
 * point `capture` just saved — so it only reaches the phone if "Guardar" is
 * pressed, exactly like every other answer typed into this card. Back out
 * without saving and the bare point stays (same as any accidental tap always
 * has), but the site itself stays pending too, ready to try again.
 */
async function captureFromPending(site: PendingSite): Promise<void> {
  const tag = tagById(site.tag);
  if (tag === null) return;
  el.pendientes.close();
  const id = await capture(tag, site.nombre ?? undefined);
  if (id !== null && draft !== null && draft.id === id) {
    draft = { ...draft, pendingId: site.id };
  }
}

/**
 * Improves the open point's position while its card is on screen.
 *
 * Only ever replaces it with a better one, and only in the draft: like every
 * other edit made while the card is open, an improved position is not written
 * to the phone unless "Guardar" is pressed. The first fix already went to the
 * phone the instant the point was pinned, so a walk-away here does not lose
 * the point — only the chance to sharpen it this time round.
 */
function refineTick(): void {
  if (draft === null || Date.now() > refineUntil) {
    if (refineLine !== null) {
      refineLine.remove();
      refineLine = null;
    }
    return;
  }
  const spot = averageSpot(buffer, Date.now());
  if (spot === null || !isBetter(spot, spotOf(draft))) return;

  draft = {
    ...draft,
    lat: round(spot.lat),
    lon: round(spot.lon),
    accuracyM: spot.accuracyM,
    spreadM: spot.spreadM,
    samples: spot.samples,
    altitudeM: spot.altitudeM,
  };
  paintSpot();
}

/* ---------------------------------------------------------------- the sheet */

function replace(point: StoredPoint): void {
  points = points.map((old) => (old.id === point.id ? point : old));
}

/**
 * Takes a change now and writes it to the phone in its own time.
 *
 * The order matters and it is the reason this exists. Written the other way
 * round — wait for the write, then update what is in memory — two answers
 * given one after the other both start from the same stale point, and the
 * second one silently erases the first. Typing how long a dry stretch runs and
 * then tapping an answer above it lost the number every time.
 */
function persist(point: StoredPoint): void {
  replace(point);
  void savePoint(point).catch((error: unknown) => {
    window.alert(
      `No se pudo guardar en el móvil: ${reason(error)}. ` +
        "Compruébalo antes de seguir: puede que este punto no se haya quedado.",
    );
  });
}

/** Repaints just the numbers and the warning of the open card, from the draft. */
function paintSpot(): void {
  if (draft === null) return;
  const detail = el.ficha.querySelector(".dato");
  const warningBox = el.ficha.querySelector<HTMLParagraphElement>(".aviso-mal");
  if (detail !== null) {
    detail.textContent = `${spotDetail(spotOf(draft))} · ${clockOf(draft.takenAt)}`;
  }
  if (refineLine !== null) refineLine.textContent = refiningText(spotOf(draft));
  const warning = spotWarning(spotOf(draft), spotQuality(spotOf(draft), draft.fixAgeS));
  if (warningBox !== null) {
    warningBox.textContent = warning ?? "";
    warningBox.hidden = warning === null;
  }
}

function choiceField(field: Field & { kind: "choice" }): HTMLElement {
  const box = document.createElement("div");
  box.className = "campo";

  const label = document.createElement("span");
  label.className = "etiqueta";
  label.textContent = field.label;
  box.appendChild(label);

  // Declared before the loop and filled in after: the buttons' click handler
  // below runs later, once this has already been assigned, so it can toggle
  // the box without the two being built in the other order.
  let detail: HTMLInputElement | null = null;

  const options = document.createElement("div");
  options.className = "opciones";
  for (const option of field.options) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "opcion";
    button.textContent = option;
    button.setAttribute("aria-pressed", String(draft?.fields[field.id] === option));
    button.addEventListener("click", () => {
      if (draft === null) return;
      // Tapping the chosen one again clears it: picking the wrong answer by
      // accident must not be something you cannot undo out in the field.
      const answers: Answers = { ...draft.fields };
      if (answers[field.id] === option) delete answers[field.id];
      else answers[field.id] = option;
      // Unchecking "otro" drops its detail too: keeping it around unattached
      // to any answer would be a stray line nobody asked for in the export.
      if (answers[field.id] !== "otro") delete answers[otherKey(field.id)];
      draft = { ...draft, fields: answers };
      for (const other of options.querySelectorAll(".opcion")) {
        other.setAttribute("aria-pressed", String(other.textContent === answers[field.id]));
      }
      if (detail !== null) {
        detail.hidden = answers[field.id] !== "otro";
        if (detail.hidden) detail.value = "";
        else detail.focus();
      }
    });
    options.appendChild(button);
  }
  box.appendChild(options);

  if (hasOther(field)) {
    detail = document.createElement("input");
    detail.type = "text";
    detail.className = "detalle-otro";
    detail.placeholder = "¿Cuál, en concreto?";
    detail.id = `campo-${field.id}-otro`;
    detail.value = String(draft?.fields[otherKey(field.id)] ?? "");
    detail.hidden = draft?.fields[field.id] !== "otro";
    detail.addEventListener("input", () => {
      if (draft === null || detail === null) return;
      const answers: Answers = { ...draft.fields };
      const text = detail.value.trim();
      if (text === "") delete answers[otherKey(field.id)];
      else answers[otherKey(field.id)] = text;
      draft = { ...draft, fields: answers };
    });
    box.appendChild(detail);
  }
  return box;
}

function numberField(field: Field & { kind: "number" }): HTMLElement {
  const box = document.createElement("div");
  box.className = "campo";

  const label = document.createElement("label");
  label.className = "etiqueta";
  label.textContent = field.label;
  box.appendChild(label);

  const row = document.createElement("div");
  row.className = "numero";
  const input = document.createElement("input");
  // Text and not `number`: a comma is what a Spanish milestone is engraved
  // with and what half the phone keyboards produce, and `type="number"`
  // silently discards the whole value when it sees one.
  input.type = "text";
  input.inputMode = "decimal";
  const currentValue = draft?.fields[field.id];
  input.value = currentValue === undefined ? "" : String(currentValue);
  input.addEventListener("input", () => {
    if (draft === null) return;
    const answers: Answers = { ...draft.fields };
    const value = readNumber(input.value);
    if (value === null) delete answers[field.id];
    else answers[field.id] = value;
    draft = { ...draft, fields: answers };
  });
  label.htmlFor = `campo-${field.id}`;
  input.id = `campo-${field.id}`;

  const unit = document.createElement("span");
  unit.className = "unidad";
  unit.textContent = field.unit;
  row.append(input, unit);
  box.appendChild(row);

  const hint = document.createElement("span");
  hint.className = "pista-campo";
  hint.textContent = field.hint;
  box.appendChild(hint);
  return box;
}

/**
 * One row of photo thumbnails plus an add button, for one kind of photo.
 *
 * Shared between the general photos and the stamp photo rather than two
 * near-identical blocks of markup: the two differ only in their cap, their
 * label and which slice of `draft.photos` they show.
 */
function photoRow(
  kind: "general" | "sello",
  cap: number,
  addLabel: (count: number) => string,
  removeLabel: (index: number) => string,
  required: boolean,
): HTMLElement {
  const box = document.createElement("div");
  box.className = "fotos";

  const paint = (): void => {
    box.textContent = "";
    if (draft === null) return;
    const mine = draft.photos.filter((photo) => photo.kind === kind);

    mine.forEach((photo, index) => {
      const url = URL.createObjectURL(photo.blob);
      const shot = document.createElement("button");
      shot.type = "button";
      shot.className = "foto-previa";
      shot.setAttribute("aria-label", removeLabel(index + 1));
      const image = document.createElement("img");
      image.src = url;
      image.alt = "";
      image.addEventListener("load", () => URL.revokeObjectURL(url), { once: true });
      shot.appendChild(image);
      shot.addEventListener("click", () => {
        if (!window.confirm("¿Quitar esta foto? Se quita en cuanto guardes la ficha.")) return;
        if (draft === null) return;
        // Removes this one photo of this kind, leaving the other kind's
        // photos — and this kind's other photos — exactly where they were.
        let seen = 0;
        draft = {
          ...draft,
          photos: draft.photos.filter((candidate) => {
            if (candidate.kind !== kind) return true;
            return seen++ !== index;
          }),
        };
        paint();
      });
      box.appendChild(shot);
    });

    if (mine.length >= cap) return;
    const add = document.createElement("button");
    add.type = "button";
    add.className = "accion foto";
    add.textContent = addLabel(mine.length);
    if (required && mine.length === 0) add.classList.add("foto-hace-falta");
    add.addEventListener("click", () => takePhoto(kind, paint));
    box.appendChild(add);
  };

  paint();
  return box;
}

function photoStrip(tag: Tag | null): HTMLElement {
  const box = document.createElement("div");
  box.appendChild(
    photoRow(
      "general",
      MAX_PHOTOS,
      (count) => (count === 0 ? "📷 Hacer foto" : "📷 Otra foto"),
      (n) => `Quitar la foto ${n}`,
      tag?.photo === "required",
    ),
  );
  if (hasSello(tag)) {
    const label = document.createElement("p");
    label.className = "etiqueta-fotos";
    label.textContent = "🔖 Foto del sello";
    box.appendChild(label);
    box.appendChild(
      photoRow(
        "sello",
        MAX_SELLO,
        () => "📷 Foto del sello",
        () => "Quitar la foto del sello",
        tag?.sello === "required",
      ),
    );
  }
  return box;
}

/**
 * Opens a point's card and starts a fresh draft for it.
 *
 * **Nothing here writes to the phone.** Every field, the note and the photos
 * edit `draft` only. The single door back out to storage is the "Guardar"
 * button below; the back gesture and the escape key both close the dialog
 * without going through it, and the shared `close` listener in `start()`
 * simply drops the draft when that happens.
 */
function openSheet(id: string): void {
  const point = find(id);
  if (point === undefined) return;
  draft = { ...point, fields: { ...point.fields }, photos: [...point.photos] };
  const tag = tagById(point.tag);

  el.ficha.textContent = "";
  refineLine = null;

  const title = document.createElement("h3");
  title.textContent = `${tag?.emoji ?? "•"} ${tag?.label ?? point.tag}`;

  const detail = document.createElement("p");
  detail.className = "dato";

  el.ficha.append(title, detail);

  if (Date.now() < refineUntil) {
    refineLine = document.createElement("p");
    refineLine.className = "afinando";
    el.ficha.appendChild(refineLine);
  }

  const warning = document.createElement("p");
  warning.className = "aviso-mal";
  el.ficha.appendChild(warning);

  for (const field of tag?.fields ?? []) {
    el.ficha.appendChild(field.kind === "choice" ? choiceField(field) : numberField(field));
  }

  if (tag !== null) {
    const framing = document.createElement("p");
    framing.className = "encuadre";
    framing.textContent = tag.framing;
    el.ficha.appendChild(framing);
  }
  el.ficha.appendChild(photoStrip(tag));

  const note = document.createElement("textarea");
  note.value = draft.note;
  note.placeholder = "Cualquier otra cosa que quieras contar";
  note.addEventListener("input", () => {
    if (draft === null) return;
    draft = { ...draft, note: note.value };
  });
  el.ficha.appendChild(note);

  const save = document.createElement("button");
  save.type = "button";
  save.className = "accion principal";
  save.textContent = "Guardar";
  // The one and only way anything typed or tapped in this card reaches the
  // phone. Everything else — the back gesture, escape, the browser closing
  // the tab — leaves the point exactly as it already was.
  save.addEventListener("click", () => {
    if (draft !== null) persist({ ...draft, note: draft.note.trim() });
    render();
    el.ficha.close();
  });

  const remove = document.createElement("button");
  remove.type = "button";
  remove.className = "accion borrar";
  remove.setAttribute("aria-label", "Borrar este punto");
  remove.textContent = "🗑";
  remove.addEventListener("click", () => {
    if (!window.confirm("¿Borrar este punto? No se puede deshacer.")) return;
    void deletePoint(id).then(() => {
      points = points.filter((candidate) => candidate.id !== id);
      draft = null;
      el.ficha.close();
      render();
    });
  });

  const actions = document.createElement("div");
  actions.className = "acciones";
  actions.append(save, remove);
  el.ficha.appendChild(actions);

  paintSpot();
  if (!el.ficha.open) el.ficha.showModal();
}

function takePhoto(kind: "general" | "sello", repaint: () => void): void {
  camera.value = "";
  camera.onchange = (): void => {
    const file = camera.files?.[0];
    if (file === undefined) return;
    void (async (): Promise<void> => {
      try {
        const small = await shrinkPhoto(file);
        if (draft === null) return;
        // Only this photo's own kind is capped and only its own kind is
        // touched: taking a stamp photo must never push a general one out.
        const cap = kind === "sello" ? MAX_SELLO : MAX_PHOTOS;
        const already = draft.photos.filter((photo) => photo.kind === kind);
        const kept = [...already, { blob: small, kind }].slice(-cap);
        const others = draft.photos.filter((photo) => photo.kind !== kind);
        draft = { ...draft, photos: [...others, ...kept] };
        repaint();
      } catch (error) {
        window.alert(
          `No se pudo guardar la foto: ${reason(error)}. ` +
            "El punto sigue apuntado; puedes volver a intentarlo.",
        );
      }
    })();
  };
  camera.click();
}

function reason(error: unknown): string {
  return error instanceof Error ? error.message : "error desconocido";
}

/* -------------------------------------------------------------- exporting */

/**
 * Hands the file to the phone.
 *
 * Sharing is tried first because on a phone it is the only thing that reaches
 * anywhere useful — mail, the cloud, a cable — while a download lands in a
 * folder some phones make genuinely hard to find. If the share sheet is
 * dismissed, nothing is marked as exported: a cancelled share is not a backup.
 */
async function handOver(name: string, zip: Uint8Array<ArrayBuffer>): Promise<boolean> {
  const file = new File([zip], name, { type: "application/zip" });
  if (navigator.canShare?.({ files: [file] }) === true) {
    try {
      await navigator.share({ files: [file], title: name });
      return true;
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return false;
      // Anything else falls through to the plain download below.
    }
  }
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  // Attached before clicking: a link that is not in the page gets ignored by
  // some browsers, and the failure is silent — no file, and no error either.
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return true;
}

async function doExport(): Promise<void> {
  const going = pending();
  if (going.length === 0) return;
  el.exportar.disabled = true;
  el.exportar.textContent = "Preparando…";
  try {
    const previewItems: ExportPreviewItem[] = going.map((stored) => {
      const { photos, exported: _exported, pendingId: _pendingId, ...point } = stored;
      return { point, photos: photos.map((photo) => ({ kind: photo.kind, bytes: photo.blob.size })) };
    });
    const estimatedBytes = estimateExportSize(previewItems, new Date());
    const storage = await navigator.storage?.estimate?.();
    if (storage?.quota !== undefined && storage.usage !== undefined) {
      const free = Math.max(0, storage.quota - storage.usage);
      if (estimatedBytes > free) {
        const proceed = window.confirm(
          `Este ZIP necesita ${bytesText(estimatedBytes)} y la PWA declara ` +
            `${bytesText(free)} libres.\n\n` +
            "No se conoce el espacio libre total del teléfono ni el de la aplicación que reciba el archivo. " +
            "Puedes intentarlo, pero podría fallar. ¿Continuar?",
        );
        if (!proceed) return;
      }
    }
    const items: ExportItem[] = [];
    for (const stored of going) {
      // `pendingId` is this app's own bookkeeping (see `types.ts`): the map
      // project on the other end never saw that registry and has no use for
      // its ids.
      const { photos, exported: _wasExported, pendingId: _pendingId, ...rest } = stored;
      const exportPhotos: ExportPhoto[] = [];
      for (const photo of photos) {
        exportPhotos.push({ bytes: new Uint8Array(await photo.blob.arrayBuffer()), kind: photo.kind });
      }
      items.push({ point: rest, photos: exportPhotos });
    }
    const { name, zip } = buildExport(items, new Date());
    if (!(await handOver(name, zip))) return;

    await markExported(going.map((point) => point.id));
    const sent = new Set(going.map((point) => point.id));
    points = points.map((point) => (sent.has(point.id) ? { ...point, exported: true } : point));
    window.alert(
      `Exportados ${going.length} puntos en ${name}.\n\n` +
        "Siguen guardados en el móvil: bórralos cuando tengas el fichero a salvo.",
    );
  } catch (error) {
    window.alert(
      `No se pudo exportar: ${reason(error)}. No se ha perdido nada; vuelve a intentarlo.`,
    );
  } finally {
    render();
  }
}


/* ------------------------------------------------------- limpiar lo apuntado */

/**
 * Borrar de verdad, por días.
 *
 * Hace falta porque los primeros puntos de cualquiera son malos: se aprende a
 * usar la app andando, y esa primera tanda no tiene por qué acabar en el mapa.
 * Borrar de uno en uno treinta puntos de prueba no lo hace nadie, así que
 * acabarían exportados «por no borrarlos», que es justo lo que hay que evitar.
 *
 * Se agrupa por días porque así es como se recuerda —«lo del sábado fue una
 * prueba»— y no por lo que sea más fácil de programar.
 */
function openCleanup(): void {
  el.limpieza.textContent = "";

  const title = document.createElement("h3");
  title.textContent = "🧹 Limpiar";
  const warning = document.createElement("p");
  warning.className = "dato";
  warning.textContent = "Lo que borres aquí no se recupera.";
  el.limpieza.append(title, warning);

  const days = new Map<string, StoredPoint[]>();
  for (const point of points) {
    const day = dayOf(point.id);
    days.set(day, [...(days.get(day) ?? []), point]);
  }

  if (days.size === 0) {
    const empty = document.createElement("p");
    empty.className = "vacio";
    empty.textContent = "No hay nada apuntado.";
    el.limpieza.appendChild(empty);
  }

  const list = document.createElement("ul");
  list.className = "dias";
  for (const [day, ofDay] of [...days.entries()].sort().reverse()) {
    const exported = ofDay.filter((point) => point.exported).length;

    const when = document.createElement("span");
    when.className = "cuando";
    when.textContent = dayLabel(day);
    const howMany = document.createElement("span");
    howMany.className = "cuantos";
    howMany.textContent = daySummary(ofDay.length, exported);
    const text = document.createElement("span");
    text.className = "texto";
    text.append(when, howMany);

    const remove = document.createElement("button");
    remove.type = "button";
    remove.className = "accion borrar";
    remove.setAttribute("aria-label", `Borrar lo del ${dayLabel(day)}`);
    remove.textContent = "🗑";
    remove.addEventListener("click", () => {
      const ask = `¿Borrar los ${ofDay.length} puntos del ${dayLabel(day)}?\n\n${deleteWarning(ofDay.length, exported)}`;
      if (!window.confirm(ask)) return;
      const ids = ofDay.map((point) => point.id);
      void deleteMany(ids).then(() => {
        const gone = new Set(ids);
        points = points.filter((point) => !gone.has(point.id));
        render();
        openCleanup();
      });
    });

    const item = document.createElement("li");
    item.className = "dia";
    item.append(text, remove);
    list.appendChild(item);
  }
  el.limpieza.appendChild(list);

  const exported = points.filter((point) => point.exported);
  if (exported.length > 0) {
    const sweep = document.createElement("button");
    sweep.type = "button";
    sweep.className = "accion";
    sweep.textContent = `Borrar solo lo ya exportado (${exported.length})`;
    sweep.addEventListener("click", () => {
      if (!window.confirm(`¿Borrar ${exported.length} puntos ya exportados?\n\n${deleteWarning(exported.length, exported.length)}`)) {
        return;
      }
      const ids = exported.map((point) => point.id);
      void deleteMany(ids).then(() => {
        const gone = new Set(ids);
        points = points.filter((point) => !gone.has(point.id));
        render();
        openCleanup();
      });
    });
    el.limpieza.appendChild(sweep);
  }

  const close = document.createElement("button");
  close.type = "button";
  close.className = "accion principal";
  close.textContent = "Cerrar";
  close.addEventListener("click", () => el.limpieza.close());
  const actions = document.createElement("div");
  actions.className = "acciones";
  actions.appendChild(close);
  el.limpieza.appendChild(actions);

  if (!el.limpieza.open) el.limpieza.showModal();
}

/* ------------------------------------------------------------- pendientes */

/**
 * The registry of known sites still missing something, ordered by km so it
 * can be worked through as the walk itself advances.
 *
 * Not the app's own points: this is a list handed over once, of alojamientos
 * and bares/restaurantes the owner already knows exist but has no full data
 * for. Tapping one starts a point the same way any button in "¿Qué has
 * visto?" does — saved at once, its card open to answer what was missing.
 */
function openPendientes(): void {
  el.pendientes.textContent = "";

  const title = document.createElement("h3");
  title.textContent = "📋 Pendientes de rellenar";
  const info = document.createElement("p");
  info.className = "dato";
  info.textContent =
    "Alojamientos, bares y restaurantes del Francés (Sarria–Santiago) a los " +
    "que les falta algún dato. Al tocar uno se abre su ficha, igual que con " +
    "cualquier otro punto.";
  el.pendientes.append(title, info);

  const left = pendingLeft();

  if (left.length === 0) {
    const empty = document.createElement("p");
    empty.className = "vacio";
    empty.textContent = "No queda ninguno por rellenar.";
    el.pendientes.appendChild(empty);
  }

  const list = document.createElement("ul");
  list.className = "lista";
  for (const site of left) {
    const tag = tagById(site.tag);

    const row = document.createElement("button");
    row.type = "button";
    row.className = "fila";

    const emoji = document.createElement("span");
    emoji.className = "emoji";
    emoji.textContent = tag?.emoji ?? "•";

    const what = document.createElement("span");
    what.className = "que";
    what.textContent = site.nombre ?? "(sin nombre)";

    const when = document.createElement("span");
    when.className = "cuando";
    when.textContent = `${pendingKm(site.km)} · ${pendingSummary(site.falta)}`;

    const text = document.createElement("span");
    text.className = "texto";
    text.append(what, document.createElement("br"), when);
    row.append(emoji, text);

    row.addEventListener("click", () => void captureFromPending(site));
    const item = document.createElement("li");
    item.appendChild(row);
    list.appendChild(item);
  }
  el.pendientes.appendChild(list);

  const close = document.createElement("button");
  close.type = "button";
  close.className = "accion principal";
  close.textContent = "Cerrar";
  close.addEventListener("click", () => el.pendientes.close());
  const actions = document.createElement("div");
  actions.className = "acciones";
  actions.appendChild(close);
  el.pendientes.appendChild(actions);

  if (!el.pendientes.open) el.pendientes.showModal();
}

/* ------------------------------------------------------------------- start */

/**
 * Asks the browser not to throw a day's walking away.
 *
 * By default a browser treats what a site stores as expendable: if the phone
 * runs short of space it clears the least-used sites without asking anybody.
 * That is fine for a shop and not fine here, where what is stored is thirty
 * points somebody walked twenty kilometres to collect.
 *
 * Asking marks the storage as persistent, and an installed app is normally
 * granted it without a prompt. If the answer is no, nothing breaks — it only
 * means exporting the same day matters more, which the app says anyway.
 */
async function keepStorage(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Not being able to ask is not a reason to stop.
  }
}

async function start(): Promise<void> {
  renderButtons();
  void keepStorage();
  points = await allPoints();
  render();

  // One listener for the whole life of the page. "Guardar" has already
  // written the draft and closed the dialog by the time this runs; every
  // other way of closing — the back gesture, escape, the tab going away —
  // reaches only this, and this never writes anything. It just clears
  // whatever card was open, so the next one starts from a clean draft.
  el.ficha.addEventListener("close", () => {
    draft = null;
    refineUntil = 0;
    refineLine = null;
  });

  watchFix(
    (reading) => {
      buffer.push(reading);
      const cutoff = Date.now() - BUFFER_MS;
      while (buffer.length > 0 && (buffer[0]?.at ?? 0) < cutoff) buffer.shift();
      problem = null;
      renderStatus();
    },
    (trouble) => {
      problem = trouble;
      renderStatus();
    },
  );

  // The margin is only half the story: a reading that was good ten minutes ago
  // is not good now, and the bar has to say so without being touched. The same
  // beat improves the point whose card is open.
  window.setInterval(() => {
    renderStatus();
    refineTick();
  }, 1000);

  window.addEventListener("online", renderFooter);
  window.addEventListener("offline", renderFooter);
  el.exportar.addEventListener("click", () => void doExport());
  el.limpiar.addEventListener("click", openCleanup);
  el.abrirPendientes.addEventListener("click", openPendientes);
}

void start();
